<script setup lang="ts">
import z, { Schema } from "zod";
import type { FormError, FormSubmitEvent } from "#ui/types";
import LogoImage from "../assets/logo.svg";
import { useStorage } from "@vueuse/core";
import PasswordResetModal from "~/components/login/ForgottenPasswordModal.vue";
import {
  LegalPrivacyPolicyModal,
  LegalTermsOfServiceModal,
  type CommonPasswordStrengthMeter,
  type UForm,
} from "#components";

useHead({
  title: () => `Login | Fight Search`,
  meta: [{ name: "robots", content: "noarchive" }],
});

useSeoMeta({
  title: () => `Login | Fight Search`,
  ogTitle: () => `Login | Fight Search`,
  description: () =>
    "Find upcoming martial arts events, tournaments and competitions.",
  ogDescription: () =>
    "Find upcoming martial arts events, tournaments and competitions.",
  twitterCard: "summary",
});

const { t } = useI18n();

const authStore = useUserStore();

const signUpState = ref(false);
const formRef = defineModel<InstanceType<typeof UForm>>();

const messageBoxProperties = reactive({
  type: "", // error/success
  label: "",
});

function resetMessageBoxProps() {
  messageBoxProperties.label = messageBoxProperties.type = "";
}

const messageBoxLabelComputed = computed(() => messageBoxProperties.label);

const rememberMeCheckboxState = ref(
  useStorage("rememberMeCheckbox", false).value,
);

const isReturningUserInitialValue = useStorage("returningUser", false).value;
useStorage("returningUser", false).value = true;

const isVisiblePasswordInputField = ref(false);
const isVisibleConfirmPasswordInputField = ref(false);

function toggleState() {
  signUpState.value = !signUpState.value;

  formRef?.value?.clear();
  state.confirmPassword = undefined;
  resetMessageBoxProps();
}

useI18n().onBeforeLanguageSwitch = async () => {
  formRef?.value?.clear();
};

const state = reactive({
  email: undefined,
  password: undefined,
  confirmPassword: undefined,
});

const onValidate = (state: any): FormError[] => {
  const errors = [];

  if (state.email) {
    if (!validateEmail(state.email)) {
      errors.push({ path: "email", message: t("error.invalid_format") });
    }
  } else {
    errors.push({ path: "email", message: t("error.mandatory") });
  }

  if (!state.password) {
    errors.push({ path: "password", message: t("error.mandatory") });
  }

  if (signUpState.value) {
    if (state.confirmPassword) {
    } else {
      errors.push({ path: "confirmPassword", message: t("error.mandatory") });
    }

    if (state.password != state.confirmPassword) {
      errors.push({
        path: "confirmPassword",
        message: t("error.password.mismatch"),
      });
    } else {
      if (!passwordMeterComponentRef?.value?.allPasswordRequirementsMet) {
        errors.push(
          {
            path: "password",
            message: t("login.error.password_requirements_not_met"),
          },
          {
            path: "confirmPassword",
            message: t("login.error.password_requirements_not_met"),
          },
        );
      }
    }
  }

  return errors;
};

async function onSubmit(formData: FormSubmitEvent<Schema>) {
  resetMessageBoxProps();
  rememberMe();

  var requestSuccess = false;
  var responseMessage = null;

  try {
    if (signUpState.value) {
      await authStore.signUp(
        formData.data.email,
        formData.data.password,
        getExplicitRedirectPath(),
      );
      responseMessage = "login.sign_up.success";
      signUpState.value = false;
    } else {
      await authStore.login(formData.data.email, formData.data.password);
      await navigateTo(getPostLoginRedirectPath());
    }

    requestSuccess = true;
  } catch (error) {
    responseMessage = "common.error";

    if (error?.code == "user_already_exists") {
      responseMessage = "error.signup.user_already_registered";
    }

    if (error?.message == "Invalid login credentials") {
      responseMessage = "error.signup.invalid_credentials";
    }
  } finally {
    authStore.isRequestInProgress = false;
  }

  if (responseMessage?.length) {
    messageBoxProperties.type = requestSuccess ? "success" : "error";
    messageBoxProperties.label = responseMessage;
  }
}

function getPostLoginRedirectPath() {
  return getExplicitRedirectPath() ?? "/";
}

function getExplicitRedirectPath() {
  const redirectQuery = route.query?.redirect;
  const redirectValue = Array.isArray(redirectQuery)
    ? redirectQuery[0]
    : redirectQuery;

  const redirectPath = String(redirectValue ?? "").trim();
  if (!redirectPath) {
    return null;
  }

  // Allow only in-app absolute paths to prevent open-redirect abuse.
  if (!redirectPath.startsWith("/") || redirectPath.startsWith("//")) {
    return null;
  }

  return redirectPath;
}

//////////////////////////////////////////////////////////////////////////
// REMEMBER ME
//////////////////////////////////////////////////////////////////////////
if (rememberMeCheckboxState.value) {
  state.email = localStorage.rememberMeEmail;
} else {
  rememberMeCheckboxState.value = false;
  state.email = undefined;
}

function rememberMe() {
  if (
    rememberMeCheckboxState.value &&
    state.email != undefined &&
    state.email !== ""
  ) {
    localStorage.rememberMeEmail = state.email;
    localStorage.rememberMeCheckbox = rememberMeCheckboxState.value;
  } else {
    localStorage.rememberMeEmail = "";
    localStorage.rememberMeCheckbox = false;
  }
}

//////////////////////////////////////////////////////////////////////////
// SIGN UP
//////////////////////////////////////////////////////////////////////////
const passwordMeterComponentRef =
  ref<InstanceType<typeof CommonPasswordStrengthMeter>>();

function togglePasswordVisibility(inputName: string) {
  var x = document.getElementsByName(inputName);

  if (inputName == "password") {
    isVisiblePasswordInputField.value = !isVisiblePasswordInputField.value;
  } else if (inputName == "confirmPassword") {
    isVisibleConfirmPasswordInputField.value =
      !isVisibleConfirmPasswordInputField.value;
  }

  x[0].type = x[0].type === "password" ? "text" : "password";
}

//////////////////////////////////////////////////////////////////////////
// FORGOT PASSWORD
//////////////////////////////////////////////////////////////////////////
const route = useRoute();

function onPressedForgotPassword() {
  useModal().open(PasswordResetModal, { email: state.email });
}

const errorQueryParam = route.query?.error;
if (errorQueryParam == "access_denied") {
  messageBoxProperties.type = "error";
  messageBoxProperties.label = "login.forgot_password.error.access_denied";
} else if (errorQueryParam == "generic") {
  messageBoxProperties.type = "error";
  messageBoxProperties.label = "common.error";
}

//////////////////////////////////////////////////////////////////////////
// GOOGLE LOGIN
//////////////////////////////////////////////////////////////////////////
const isGoogleLoginEnabled = useRuntimeConfig().public.googleLoginEnabled;
const loading = ref(false);

async function signInWithGoogle() {
  loading.value = true;
  const confirmUrl = new URL(
    "confirm",
    useRuntimeConfig().public.supabaseRedirectBaseUrl,
  );
  const redirectPath = getExplicitRedirectPath();

  if (redirectPath) {
    confirmUrl.searchParams.set("redirect", redirectPath);
  }

  const { error } = await useSupabaseClient().auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: confirmUrl.toString(),
    },
  });

  if (error) {
    displayErrorNotification();
    usePosthog().logError(error, "google_oauth_signin_error");
  } else {
    loading.value = false;
  }
}

//////////////////////////////////////////////////////////////////////////
// OTHER
//////////////////////////////////////////////////////////////////////////
function openTosModal() {
  useModal().open(LegalTermsOfServiceModal);
}

function openPrivacyPolicyModal() {
  useModal().open(LegalPrivacyPolicyModal);
}
</script>

<template>
  <div class="flex flex-col items-center justify-center min-h-[100dvh]">
    <UCard>
      <div class="space-y-6 h-full max-w-sm">
        <div class="flex flex-col items-center">
          <Icon
            name="assets:logo"
            class="logo w-24 min-h-24 rounded-full bg-primary-500"
            :fontControlled="false"
          />

          <p class="text-2xl text-gray-900 dark:text-white font-bold mt-6">
            {{
              isReturningUserInitialValue
                ? t("login.label.welcome_back")
                : t("login.label.welcome")
            }}
          </p>

          <div
            class="flex flex-row items-center text-sm gap-1 text-gray-500 dark:text-gray-400 mt-1"
          >
            <span v-if="signUpState">{{
              $t("login.already_have_an_account")
            }}</span>
            <span v-else>{{ $t("login.dont_have_an_account") }}</span>

            <UButton
              variant="link"
              size="sm"
              :padded="false"
              :label="signUpState ? t('login.login') : t('login.sign_up')"
              @click="toggleState"
            />
          </div>
        </div>

        <div
          v-if="messageBoxLabelComputed !== ''"
          :class="
            messageBoxProperties.type == 'success'
              ? 'bg-primary-500 dark:bg-primary-400 w-full relative overflow-hidden rounded-md p-4 text-white dark:text-gray-900'
              : 'bg-red-500 dark:bg-red-400 w-full relative overflow-hidden rounded-md p-4 text-white dark:text-gray-900'
          "
        >
          <div class="flex gap-2 items-center">
            <UIcon
              :name="
                messageBoxProperties.type == 'success'
                  ? 'i-heroicons-check-circle-16-solid'
                  : 'i-heroicons-information-circle-20-solid'
              "
              class="w-5 h-5"
            />
            <p class="flex-1 text-sm font-medium">
              <i18n-t tag="span" :keypath="messageBoxLabelComputed">
                <template v-slot:br><br /></template>
              </i18n-t>
            </p>
          </div>
        </div>

        <div>
          <UForm
            :state="state"
            class="space-y-4"
            :validate="onValidate"
            :validateOn="['submit']"
            @submit="onSubmit"
            ref="formRef"
            novalidate
          >
            <UFormGroup :label="t('common.email')" name="email">
              <UInput v-model="state.email" type="email" tabindex="5" />
            </UFormGroup>

            <UFormGroup :label="t('common.password')" name="password">
              <template #hint>
                <UButton
                  v-if="!signUpState"
                  variant="link"
                  size="sm"
                  :label="t('login.forgotten_password')"
                  :padded="false"
                  @click="
                    () => {
                      onPressedForgotPassword();
                    }
                  "
                />
              </template>

              <UInput
                v-model="state.password"
                type="password"
                autocomplete="off"
                :ui="{ icon: { trailing: { pointer: '' } } }"
                tabindex="5"
              >
                <template #trailing>
                  <UButton
                    color="gray"
                    variant="link"
                    :icon="
                      isVisiblePasswordInputField
                        ? 'i-heroicons-eye'
                        : 'i-heroicons-eye-slash'
                    "
                    :padded="false"
                    @click="
                      () => {
                        togglePasswordVisibility('password');
                      }
                    "
                  />
                </template>
              </UInput>
            </UFormGroup>

            <UFormGroup
              v-if="signUpState"
              :label="t('common.password.confirm')"
              name="confirmPassword"
            >
              <UInput
                v-model="state.confirmPassword"
                type="password"
                autocomplete="off"
                :ui="{ icon: { trailing: { pointer: '' } } }"
                tabindex="5"
              >
                <template #trailing>
                  <UButton
                    color="gray"
                    variant="link"
                    :icon="
                      isVisibleConfirmPasswordInputField
                        ? 'i-heroicons-eye'
                        : 'i-heroicons-eye-slash'
                    "
                    :padded="false"
                    @click="
                      () => {
                        togglePasswordVisibility('confirmPassword');
                      }
                    "
                  />
                </template>
              </UInput>
            </UFormGroup>

            <CommonPasswordStrengthMeter
              :class="signUpState ? 'block' : 'hidden'"
              ref="passwordMeterComponentRef"
              v-model:new-password="state.password"
              v-model:confirm-password="state.confirmPassword"
            />

            <UCheckbox
              v-if="!signUpState"
              v-model="rememberMeCheckboxState"
              name="remember-me"
              :label="$t('login.remember_me')"
            />

            <UButton
              block
              :loading="authStore.isRequestInProgress"
              type="submit"
              :label="signUpState ? t('login.sign_up') : t('login.login')"
            />
          </UForm>
        </div>

        <UDivider
          v-if="isGoogleLoginEnabled"
          :label="$t('common.or').toUpperCase()"
          :ui="{ label: 'text-xs text-gray-400 dark:text-gray-500' }"
        />

        <div
          v-if="isGoogleLoginEnabled"
          class="flex flex-row gap-2 justify-center"
        >
          <UButton
            icon="i-logos-google-icon"
            @click="signInWithGoogle"
            :loading="loading"
            color="white"
            variant="link"
          />
        </div>

        <p class="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
          {{
            $t(
              signUpState
                ? "login.sign_up.terms_and_conditions.prefix"
                : "login.login.terms_and_conditions.prefix",
            )
          }}
          <UButton
            variant="link"
            size="sm"
            :padded="false"
            :label="t('footer.terms_and_conditions')"
            @click="openTosModal"
          />
          &
          <UButton
            variant="link"
            size="sm"
            :padded="false"
            :label="t('footer.privacy_policy')"
            @click="openPrivacyPolicyModal"
          />.
        </p>

        <div class="flex flex-col items-center">
          <CommonLanguagePicker />
        </div>
      </div>
    </UCard>

    <div class="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
      © {{ new Date().getFullYear() }}
      <a
        href="https://www.fight-search.com/"
        class="text-primary-500 hover:underline"
        target="_blank"
        >Fight Search</a
      >
    </div>
  </div>
</template>
